import { useState } from 'react';
import {
  Alert,
  AlertDescription,
  Badge,
  Box,
  Button,
  Center,
  Code,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Icon,
  Progress,
  Spinner,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Link as RouterLink } from 'react-router-dom';
import { ChevronDown, ChevronUp, ExternalLink, Settings } from 'lucide-react';

import { trpcAdmin, type RouterOutputs } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { JsonViewer } from '@duke-hq/ui/components/JsonViewer/JsonViewer';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { UnitGenerationRunStatus } from '@duke-hq/svc-api-db/client';

import { getStatusDisplay, reviewGate } from '../statusConfig';
import { useGenerationProgressSubscription } from '../useGenerationProgressSubscription';
import { CoverageMatrixSection } from './CoverageMatrixSection';

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Flex justify="space-between" align="start" py={2} borderBottomWidth="1px">
      <Text fontSize="sm" fontWeight="medium" color="fg.subtle">
        {label}
      </Text>
      <Box textAlign="right" flex={1}>
        {children}
      </Box>
    </Flex>
  );
}

function Section({ children }: { children: React.ReactNode }) {
  return <Box mt={6}>{children}</Box>;
}

export type RunDetailData = RouterOutputs['unitsOfCompetency']['generationById'];

export function RunDetailDrawerContent({
  run,
  onCancel,
  isCancelling,
  onReviewAllocation,
}: {
  run: RunDetailData;
  onCancel?: () => void;
  isCancelling?: boolean;
  onReviewAllocation: () => void;
}) {
  const gate = reviewGate(run);
  const statusDisplay = getStatusDisplay(run.status, run.error, gate);
  const showCancel =
    (run.status === UnitGenerationRunStatus.RUNNING ||
      run.status === UnitGenerationRunStatus.WAITING) &&
    onCancel != null;
  const showReviewAllocation = gate === 'allocation';
  const showViewUnit = run.unitOfCompetencyId != null;

  return (
    <VStack align="stretch" spacing={0}>
      {/* -- Header: unit code, status, progress ------ */}
      <Box pb={4} borderBottomWidth="1px">
        <Box mb={3}>
          <HStack justify="space-between" align="center">
            <Text fontSize="lg" fontWeight="bold" fontFamily="mono">
              {run.unitCode}
            </Text>
            <Badge
              colorScheme={statusDisplay.colorScheme}
              fontSize="sm"
              px={2}
              py={0.5}
              flexShrink={0}
            >
              {statusDisplay.label}
            </Badge>
          </HStack>

          {run.unitTitle != null ? (
            <Text fontSize="sm" color="fg.subtle">
              {run.unitTitle}
            </Text>
          ) : null}
        </Box>

        {run.message != null ? (
          <Text fontSize="sm" color="fg.muted" mb={2}>
            {run.message}
          </Text>
        ) : null}

        {run.error != null ? (
          <Alert status="error" fontSize="sm" mb={2} borderRadius="md">
            <AlertDescription>{run.error}</AlertDescription>
          </Alert>
        ) : null}

        {run.progress < 100 ? (
          <HStack spacing={2} align="center">
            <Progress
              value={run.progress}
              size="sm"
              colorScheme={statusDisplay.colorScheme}
              borderRadius="full"
              flex={1}
            />
            <Text fontSize="sm" fontWeight="medium" color="fg.muted" minW="40px" textAlign="right">
              {run.progress}%
            </Text>
          </HStack>
        ) : null}
      </Box>

      {/* -- Timing ------ */}
      <Section>
        <DetailRow label={t`Started`}>
          <Text fontSize="sm">{new Date(run.createdAt).toLocaleString()}</Text>
        </DetailRow>
        <DetailRow label={t`Completed`}>
          <Text fontSize="sm">
            {run.completedAt != null ? new Date(run.completedAt).toLocaleString() : '-'}
          </Text>
        </DetailRow>
        <DetailRow label={t`Created by`}>
          <Text fontSize="sm">
            {run.createdBy.first_name} {run.createdBy.last_name}
            {run.createdBy.email != null ? ` (${run.createdBy.email})` : ''}
          </Text>
        </DetailRow>

        {/* -- Actions ------ */}
        {showCancel || showReviewAllocation || showViewUnit ? (
          <HStack pt={3} justify="flex-end" spacing={2}>
            {showCancel ? (
              <Button
                variant="outline"
                colorScheme="red"
                size="sm"
                onClick={onCancel}
                isLoading={isCancelling}
              >
                <Trans>Cancel</Trans>
              </Button>
            ) : null}

            {showReviewAllocation ? (
              <Button size="sm" colorScheme="brand" onClick={onReviewAllocation}>
                <Trans>Review allocation</Trans>
              </Button>
            ) : null}

            {showViewUnit ? (
              <Button
                as={RouterLink}
                to={`/units/${run.unitOfCompetencyId}`}
                size="sm"
                colorScheme="brand"
                rightIcon={<ExternalLink size={14} />}
              >
                <Trans>View Unit</Trans>
              </Button>
            ) : null}
          </HStack>
        ) : null}
      </Section>
    </VStack>
  );
}

export function DebuggingSection({ run }: { run: RunDetailData }) {
  const debugDisclosure = useDisclosure();

  const hasContent = run.traceId != null || run.webhookPayload != null;

  if (!hasContent) {
    return null;
  }

  return (
    <Box mt={3}>
      <Button
        size="sm"
        variant="ghost"
        onClick={debugDisclosure.onToggle}
        rightIcon={<Icon as={debugDisclosure.isOpen ? ChevronUp : ChevronDown} boxSize={4} />}
        leftIcon={<Icon as={Settings} boxSize={4} />}
        color="fg.muted"
        fontWeight="medium"
        px={0}
        _hover={{ bg: 'transparent', color: 'fg.default' }}
      >
        <Trans>Debugging</Trans>
      </Button>

      <Collapse in={debugDisclosure.isOpen} animateOpacity>
        <VStack align="stretch" spacing={0} pt={1}>
          <DetailRow label={t`Run ID`}>
            <Code fontSize="xs">{run.id}</Code>
          </DetailRow>

          {run.traceId != null ? (
            <DetailRow label={t`Trace ID`}>
              <Code fontSize="xs">{run.traceId}</Code>
            </DetailRow>
          ) : null}

          {run.webhookPayload != null ? (
            <Box py={2} borderBottomWidth="1px">
              <JsonViewer
                data={run.webhookPayload}
                title="Trigger Input"
                colorMode="dark"
                maxHeight="400px"
              />
            </Box>
          ) : null}
        </VStack>
      </Collapse>
    </Box>
  );
}

type RunDetailDrawerProps = {
  runId: string;
  isOpen: boolean;
  onClose: () => void;
  onReviewAllocation: () => void;
};

export function RunDetailDrawer({
  runId,
  isOpen,
  onClose,
  onReviewAllocation,
}: RunDetailDrawerProps) {
  const utils = trpcAdmin.useUtils();
  const toast = useToast();
  const cancelConfirmDisclosure = useDisclosure();
  const [matrixExpanded, setMatrixExpanded] = useState(false);

  const {
    data: run,
    isLoading,
    isError,
    error,
  } = trpcAdmin.unitsOfCompetency.generationById.useQuery({
    id: runId,
  });

  useGenerationProgressSubscription(runId, run?.status, run?.error);

  const runMutationOptions = (successTitle: string, errorTitle: string) => ({
    onSuccess: () => {
      toast({ status: 'success', title: successTitle });

      void utils.unitsOfCompetency.listGenerations.invalidate();
      void utils.unitsOfCompetency.generationById.invalidate({ id: runId });
    },
    onError: (error: { message: string }) => {
      toast({ status: 'error', title: errorTitle, description: error.message });
    },
  });

  const cancelMutation = trpcAdmin.unitsOfCompetency.cancelGeneration.useMutation(
    runMutationOptions(t`Generation cancelled`, t`Failed to cancel`),
  );

  const handleCancel = () => {
    cancelMutation.mutate({ id: runId });
    cancelConfirmDisclosure.onClose();
  };

  const hasCompletedWithArtifact = run?.status === UnitGenerationRunStatus.IMPORTED;

  const drawerSize = matrixExpanded ? 'full' : 'lg';

  if (isLoading) {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            <Trans>Run Details</Trans>
          </DrawerHeader>
          <DrawerBody>
            <Center p={8}>
              <Spinner size="lg" />
            </Center>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  if (isError || run === undefined) {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            <Trans>Run Details</Trans>
          </DrawerHeader>
          <DrawerBody>
            <Alert status="error">
              <AlertDescription>{error?.message ?? t`Failed to load run details`}</AlertDescription>
            </Alert>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size={drawerSize}>
      <DrawerOverlay />
      <DrawerContent
        maxW={matrixExpanded ? '95vw' : undefined}
        sx={{ transition: 'max-width 0.2s ease' }}
      >
        <DrawerCloseButton />
        <DrawerHeader>
          <Trans>Unit Generation Details</Trans>
        </DrawerHeader>
        <DrawerBody>
          <RunDetailDrawerContent
            run={run}
            onCancel={cancelConfirmDisclosure.onOpen}
            isCancelling={cancelMutation.isPending}
            onReviewAllocation={onReviewAllocation}
          />

          {hasCompletedWithArtifact ? (
            <CoverageMatrixSection runId={runId} onExpandChange={setMatrixExpanded} />
          ) : null}

          <DebuggingSection run={run} />

          <ConfirmationDialog
            isOpen={cancelConfirmDisclosure.isOpen}
            onClose={cancelConfirmDisclosure.onClose}
            title={<Trans>Cancel Generation</Trans>}
            body={
              <>
                <Text>
                  <Trans>
                    Cancel generation of <strong>{run.unitCode}</strong>?
                  </Trans>
                </Text>
                {run.unitOfCompetencyId != null ? (
                  <Text mt={2}>
                    <Trans>This will also archive the attached generated unit.</Trans>
                  </Text>
                ) : null}
              </>
            }
            onConfirm={handleCancel}
            confirmLabel={<Trans>Cancel</Trans>}
            cancelLabel={<Trans>Go Back</Trans>}
            variant="destructive"
            isLoading={cancelMutation.isPending}
          />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
